import React from "react";
import { Container, Box, Grid, Card, Typography, Stack, Divider } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SectionHeading from "./SectionHeading";
import CountUp from "./CountUp";
import { colors, fonts, ui } from "../theme";

const stats = [
  { value: "1000+", label: "Advanced Students Trained" },
  { value: "250+", label: "Students in a Single Partnership" },
  { value: "35%", label: "Rise in Day-One Placements" },
  { value: "100%", label: "Unique Student GitHub Repos" },
];

const caseStudy = {
  title: "Muthayammal Engineering College — Department of IT",
  objective:
    "A structured, long-term academic partnership to upgrade student technical capability, improve final-year projects and generate reliable audit documentation.",
  activities: [
    "Signed an official departmental MoU",
    "Integrated advanced full-stack development tracks",
    "Conducted hands-on Docker & Kubernetes workshops",
    "Provided continuous engineering-project mentoring",
  ],
  outcomes: [
    "250+ students trained",
    "35% increase in day-one technical placements",
    "100% unique student code repositories on GitHub",
    "Clear data records for compliance reviews",
  ],
  future: "Co-developing an MLOps curriculum, shared research publications, and an on-campus Innovation & Cloud Excellence Hub.",
};

const Impact = () => {
  return (
    <section id="impact-section" className="impact-section">
      <Box sx={{ py: { xs: 5, md: 7 } }}>
        <Container>
          <SectionHeading
            eyebrow="Impact Snapshot"
            title="Verified Institutional Performance Since 2024"
            subtitle="Real numbers from real partnerships — across Node.js, Cloud, DSA, AI, MLOps and DevOps tracks."
          />

          {/* Stats band */}
          <Box
            sx={{
              background: colors.brandGradient,
              borderRadius: 4,
              p: { xs: 3, md: 4 },
              mb: 6,
            }}
          >
            <Grid container spacing={2}>
              {stats.map((s) => (
                <Grid item xs={6} md={3} key={s.label} sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{ fontFamily: fonts.heading, fontWeight: 800, color: colors.white, fontSize: { xs: "1.8rem", md: "2.4rem" } }}
                  >
                    <CountUp value={s.value} />
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.95)", fontSize: "0.86rem", fontWeight: 500 }}>
                    {s.label}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Case study */}
          <Card
            elevation={0}
            sx={{ ...ui.cardHover, borderRadius: 4, border: "1px solid #e6edf7", overflow: "hidden" }}
          >
            <Box sx={{ background: colors.navy, color: colors.white, px: { xs: 3, md: 4 }, py: 2.5 }}>
              <Typography sx={{ color: colors.blue, fontWeight: 700, fontSize: "0.78rem", letterSpacing: "0.1em", mb: 0.5 }}>
                PARTNERSHIP CASE STUDY
              </Typography>
              <Typography sx={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: "1.2rem" }}>
                {caseStudy.title}
              </Typography>
            </Box>
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Typography sx={{ color: colors.textBody, lineHeight: 1.7, mb: 3 }}>
                <strong style={{ color: colors.navy }}>Objective — </strong>
                {caseStudy.objective}
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontFamily: fonts.heading, fontWeight: 700, color: colors.navy, mb: 1.5 }}>
                    Activities Conducted
                  </Typography>
                  <Stack spacing={1.2}>
                    {caseStudy.activities.map((a) => (
                      <Stack direction="row" spacing={1} key={a} alignItems="flex-start">
                        <CheckCircleIcon sx={{ fontSize: 18, color: colors.blue, mt: "2px" }} />
                        <Typography sx={{ fontSize: "0.92rem", color: colors.textBody }}>{a}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontFamily: fonts.heading, fontWeight: 700, color: colors.navy, mb: 1.5 }}>
                    Outcomes Achieved
                  </Typography>
                  <Stack spacing={1.2}>
                    {caseStudy.outcomes.map((o) => (
                      <Stack direction="row" spacing={1} key={o} alignItems="flex-start">
                        <CheckCircleIcon sx={{ fontSize: 18, color: colors.blue, mt: "2px" }} />
                        <Typography sx={{ fontSize: "0.92rem", color: colors.textBody }}>{o}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
              <Divider sx={{ my: 3 }} />
              <Typography sx={{ color: colors.textMuted, fontSize: "0.92rem", lineHeight: 1.7 }}>
                <strong style={{ color: colors.navy }}>Future collaboration — </strong>
                {caseStudy.future}
              </Typography>
            </Box>
          </Card>
        </Container>
      </Box>
    </section>
  );
};

export default Impact;
